import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import img from "./all_bg.jpg";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/* ===================== BACKEND ===================== */

const BACKEND_URL = "https://api.bbsfacultyprofile.com";

/* ===================== AACSB DATE RANGE ===================== */

const AACSB_START = new Date("2020-07-01");
const AACSB_END = new Date("2026-06-31");

/* ===================== COLUMN SCHEMAS ===================== */

const SECTION_COLUMN_SCHEMA = {
  journals: [
    { key: "q1_top", label: "Q1 (Top 10%)" },
    { key: "q1", label: "Q1 (75–89%)" },
    { key: "q2", label: "Q2 (50–74%)" },
    { key: "q3", label: "Q3 (25–49%)" },
    { key: "q4", label: "Q4 (0–24%)" },
    { key: "unknown", label: "Unknown" },
  ],

  books: [
    { key: "book_intl", label: "International" },
    { key: "book_local", label: "Local" },
  ],

  chapters: [
    { key: "chapter_bd", label: "Local" },
    { key: "chapter_intl", label: "International" },
  ],

  book_review: [
    { key: "book_review_intl", label: "Book Review International" },
    { key: "book_review_local", label: "Book Review Local" },
  ],

  conferences: [
    { key: "conf_proc_intl", label: "Proceeding Intl" },
    { key: "conf_proc_bd", label: "Proceeding Local" },
    { key: "conf_pres_intl", label: "Presentation Intl" },
    { key: "conf_pres_bd", label: "Presentation Local" },
  ],

  trade: [
    { key: "trade_intl", label: "International" },
    { key: "trade_bd", label: "Local" },
  ],

  others: [
    { key: "industry_reports", label: "Industry Reports" },
    { key: "opeds", label: "Press articles (op-eds/editorials)" },
  ],

  textbooks: [
    { key: "textbook_new", label: "New Publication" },
    { key: "textbook_revision", label: "Revision" },
  ],

  cases: [
    { key: "case_indexed", label: "Published in Indexed Journal" },
    {
      key: "case_nonindexed_used",
      label: "Published in Non-Indexed Journal but used in a Course",
    },
    { key: "case_nonindexed", label: "Published in Non-Indexed" },
  ],

  grants: [
    { key: "grant_15m", label: "BDT 1.5 million and above" },
    { key: "grant_08_149", label: "BDT 0.8 million to 1.499 million" },
    { key: "grant_03_079", label: "BDT 0.3–0.799M" },
    { key: "grant_01_029", label: "BDT 0.3 million to 0.799 million" },
  ],

  editorial_editor: [
    { key: "q1_top", label: "Q1 Top 10%" },
    { key: "q1", label: "Q1 75–89%" },
    { key: "q2", label: "Q2 50–74%" },
    { key: "q3", label: "Q3 25–49%" },
    { key: "q4", label: "Q4 0–24%" },
    { key: "unknown", label: "Unknown" },
  ],

  editorial_special: [
    { key: "q1_top", label: "Q1 Top 10%" },
    { key: "q1", label: "Q1 75–89%" },
    { key: "q2", label: "Q2 50–74%" },
    { key: "q3", label: "Q3 25–49%" },
    { key: "q4", label: "Q4 0–24%" },
    { key: "unknown", label: "Unknown" },
  ],

  editorial_board: [
    { key: "q1_top", label: "Q1 Top 10%" },
    { key: "q1", label: "Q1 75–89%" },
    { key: "q2", label: "Q2 50–74%" },
    { key: "q3", label: "Q3 25–49%" },
    { key: "q4", label: "Q4 0–24%" },
    { key: "unknown", label: "Unknown" },
  ],

  editorial_reviewer: [
    { key: "q1_top", label: "Q1 Top 10%" },
    { key: "q1", label: "Q1 75–89%" },
    { key: "q2", label: "Q2 50–74%" },
    { key: "q3", label: "Q3 25–49%" },
    { key: "q4", label: "Q4 0–24%" },
    { key: "unknown", label: "Unknown" },
  ],

  conference_review: [
    { key: "local", label: "Local" },
    { key: "intl", label: "International" },
  ],

  trade_editorial: [
    { key: "local", label: "Local" },
    { key: "intl", label: "International" },
  ],

  trade_board: [
    { key: "local", label: "Local" },
    { key: "intl", label: "International" },
  ],

  trade_review: [
    { key: "local", label: "Local" },
    { key: "intl", label: "International" },
  ],

  grant_evaluation: [{ key: "count", label: "Grant Reviews" }],

  supervision_phd: [
    { key: "chair", label: "Chair/Main Supervisor" },
    { key: "member", label: "Member/Co-Supervisor" },
  ],

  supervision_masters: [
    { key: "chair", label: "Chair/Main Supervisor" },
    { key: "member", label: "Member/Co-Supervisor" },
  ],

  proposal_writing_internal: [{ key: "internal", label: "Internal ≥0.5M" }],

  proposal_writing_external: [{ key: "external", label: "External ≥1M" }],

  conference_org: [
    { key: "chair", label: "Chair" },
    { key: "cochair", label: "Co-Chair" },
    { key: "member", label: "Committee Member" },
  ],

  keynote: [
    { key: "local", label: "Local" },
    { key: "intl", label: "International" },
  ],

  program_dev: [
    { key: "count", label: "Programs Led" },
    { key: "member", label: "Programs (Member)" },
  ],

  course_dev: [
    { key: "lead_new", label: "Lead – Newly Developed" },
    { key: "lead_revision", label: "Lead – Revision" },
    { key: "member_new", label: "Member – Newly Developed" },
    { key: "member_revision", label: "Member – Revision" },
    { key: "course_coordination", label: "Course Coordination" },
    { key: "total", label: "Total Courses" },
  ],

  visiting_scholar: [
    { key: "count", label: "Number of Visiting Scholar Appointments" },
    { key: "total_years", label: "Total Visiting Scholar Duration (Years)" },
  ],

  academic_admin_role_years: [
    { key: "academic_admin_role_years", label: "Admin Role Years" },
  ],

  consultation_hours: [
    { key: "consultation_hours", label: "Consultation Hours" },
  ],

  industry_fulltime_years: [
    { key: "industry_fulltime_years", label: "Full-time Industry Years" },
  ],

  industry_parttime_years: [
    { key: "industry_parttime_years", label: "Part-time Industry Years" },
  ],

  business_ownership_years: [
    { key: "business_ownership_years", label: "Business Ownership Years" },
  ],

  board_chairman_count: [
    { key: "board_chairman_count", label: "Board Chairman Count" },
  ],

  board_member_count: [
    { key: "board_member_count", label: "Board Member Count" },
  ],

  organizational_membership_count: [
    {
      key: "organizational_membership_count",
      label: "Organizational Membership Count",
    },
  ],

  speaker_intl_count: [
    { key: "speaker_intl_count", label: "Invited Intl Speaker" },
  ],

  speaker_local_count: [
    { key: "speaker_local_count", label: "Invited Local Speaker" },
  ],

  training_sessions_count: [
    { key: "training_sessions_count", label: "Training Sessions Delivered" },
  ],

  media_contributions_count: [
    { key: "media_contributions_count", label: "Media Contributions – BRACU" },
  ],

  prof_cert_intl_count: [
    { key: "prof_cert_intl_count", label: "Intl Professional Certifications" },
  ],

  prof_cert_local_count: [
    {
      key: "prof_cert_local_count",
      label: "Local Professional Certifications",
    },
  ],

  prof_cert_total_years: [
    {
      key: "prof_cert_total_years",
      label: "Professional Certification Total Years",
    },
  ],

  new_exec_courses_count: [
    { key: "count", label: "New Executive Education Courses" },
  ],

  policy_advisory_roles_count: [
    { key: "policy_advisory_roles_count", label: "Policy Advisory Roles" },
  ],

  policy_briefs_intl: [
    { key: "policy_briefs_intl", label: "Intl Policy Briefs" },
  ],

  policy_briefs_local: [
    { key: "policy_briefs_local", label: "Local Policy Briefs" },
  ],

  ip_commercialization_count: [
    { key: "ip_commercialization_count", label: "IP Commercialization Count" },
  ],

  pro_bono_count: [
    { key: "pro_bono_count", label: "Pro-bono Services Count" },
  ],

  collaborative_count: [
    { key: "collaborative_count", label: "Total Collaborative Works" },
  ],
};

/* ===================== HELPERS ===================== */

const list = (v) => (Array.isArray(v) ? v : []);

const str = (v) => (v || "").toString().trim().toLowerCase();

const computeDurationCapped = (start, end) => {
  if (!start) return 0;

  const startDate = new Date(start);

  if (isNaN(startDate.getTime())) return 0;

  let endDate = end ? new Date(end) : new Date(AACSB_END);

  if (isNaN(endDate.getTime())) endDate = new Date(AACSB_END);

  if (endDate > AACSB_END) endDate = new Date(AACSB_END);

  if (startDate < AACSB_START) startDate.setTime(AACSB_START.getTime());

  if (endDate < startDate) return 0;

  return (
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 365)
  );
};

const withinRange = (value) => {
  if (!value) return false;

  const d = new Date(value);

  if (isNaN(d.getTime())) return false;

  return d >= AACSB_START && d <= AACSB_END;
};

const isBD = (country) => {
  const c = str(country);

  return c === "bangladesh" || c === "bd" || c.includes("bangladesh");
};

const isBangladesh = (country) => isBD(country);

const emptyQuartile = () => ({
  q1_top: 0,
  q1: 0,
  q2: 0,
  q3: 0,
  q4: 0,
  unknown: 0,
});

const normalizeQuartile = (q) => {
  const value = str(q);

  if (!value) return null;

  if (value.includes("top 10")) return "q1_top";
  if (value.includes("75") || value.includes("89")) return "q1";
  if (value.includes("50") || value.includes("74")) return "q2";
  if (value.includes("25") || value.includes("49")) return "q3";
  if (value.includes("0") || value.includes("24")) return "q4";

  return null;
};

const normalizeExpType = (value) => str(value).replace(/[\s-]/g, "");

/*
  Sums the AACSB-capped duration (years) of records whose
  start/end dates overlap the AACSB window.
*/
const overlapYears = (rows, filter = () => true) =>
  list(rows).reduce((sum, r) => {
    if (!filter(r)) return sum;

    const start = r.starting_date || r.start_date || r.starting_month_year;
    const end = r.ending_date || r.end_date || r.ending_month_year;

    if (!start) return sum;

    if (
      new Date(end || AACSB_END) < AACSB_START ||
      new Date(start) > AACSB_END
    ) {
      return sum;
    }

    return sum + computeDurationCapped(start, end);
  }, 0);

const reviewInRange = (r) => {
  const year = Number(r.review_year);

  return (
    Number.isFinite(year) &&
    year >= AACSB_START.getFullYear() &&
    year <= AACSB_END.getFullYear()
  );
};

/*
 * Book-review records are returned from the API for all faculties.
 * This helper makes sure that only records belonging to the currently
 * opened faculty are counted.
 *
 * The dashboard gets the current faculty PIN from localStorage("aacsbPin").
 * The book-review API records are expected to contain the faculty PIN in
 * the "pin" field.
 */
const bookReviewMatchesFacultyPin = (review, facultyPin) => {
  if (!facultyPin || !review) return false;

  return str(review.pin) === str(facultyPin);
};

/* ===================== COMPUTE METRICS ===================== */

const computeMetrics = (data) => {
  const result = {};

  /* ---- Collaborative works ---- */

  let collaborativeCount = 0;

  [
    "academic_journal_articles",
    "book",
    "book_chapters",
    "case_publications",
    "op_ed",
    "text_book",
    "trade_journal",
  ].forEach((table) => {
    list(data[table]).forEach((r) => {
      if (str(r.is_collaborative) === "yes") collaborativeCount++;
    });
  });

  result.collaborative_count = { collaborative_count: collaborativeCount };

  /* ---- 1. Journals ---- */

  const journals = emptyQuartile();

  list(data.academic_journal_articles).forEach((j) => {
    if (!withinRange(j.year)) return;

    const bucket = normalizeQuartile(j.quartile);

    if (bucket) journals[bucket]++;
    else journals.unknown++;
  });

  result.journals = journals;

  /* ---- 2. Books ---- */

  const books = { book_intl: 0, book_local: 0 };

  list(data.book).forEach((b) => {
    if (!withinRange(b.year)) return;

    if (isBangladesh(b.country)) books.book_local++;
    else books.book_intl++;
  });

  result.books = books;

  /* ---- 3. Book chapters ---- */

  const chapters = { chapter_bd: 0, chapter_intl: 0 };

  list(data.book_chapters).forEach((c) => {
    if (!withinRange(c.year)) return;

    if (isBangladesh(c.country)) chapters.chapter_bd++;
    else chapters.chapter_intl++;
  });

  result.chapters = chapters;

  /* ---- 4A–4B. Book reviews (authenticated API data, review_year) ---- */

  result.book_review = {
    book_review_intl: list(data.book_review_international).filter(
      (review) =>
        bookReviewMatchesFacultyPin(review, data.__facultyPin) &&
        reviewInRange(review),
    ).length,

    book_review_local: list(data.book_review_local).filter(
      (review) =>
        bookReviewMatchesFacultyPin(review, data.__facultyPin) &&
        reviewInRange(review),
    ).length,
  };

  /* ---- 5–6. Conferences ---- */

  const conferences = {
    conf_proc_intl: 0,
    conf_proc_bd: 0,
    conf_pres_intl: 0,
    conf_pres_bd: 0,
  };

  list(data.conference_proceeding).forEach((c) => {
    if (!withinRange(c.conference_date)) return;

    const intl = !isBD(c.country);

    const hasProceedings =
      c.proceedings_link && c.proceedings_link.toString().trim() !== "";

    if (hasProceedings) {
      if (intl) conferences.conf_proc_intl++;
      else conferences.conf_proc_bd++;
    } else if (intl) {
      conferences.conf_pres_intl++;
    } else {
      conferences.conf_pres_bd++;
    }
  });

  result.conferences = conferences;

  /* ---- 7. Trade journals ---- */

  const trade = { trade_intl: 0, trade_bd: 0 };

  list(data.trade_journal).forEach((t) => {
    if (!withinRange(t.year)) return;

    if (isBD(t.country)) trade.trade_bd++;
    else trade.trade_intl++;
  });

  result.trade = trade;

  /* ---- 8–9. Industry reports / op-eds ---- */

  result.others = {
    industry_reports: list(data.industry_report).filter((r) =>
      withinRange(r.year),
    ).length,
    opeds: list(data.op_ed).filter((r) => withinRange(r.year)).length,
  };

  /* ---- 10. Textbooks ---- */

  const textbooks = { textbook_new: 0, textbook_revision: 0 };

  list(data.text_book).forEach((t) => {
    if (!withinRange(t.year)) return;

    const type = str(t.type);

    if (type.includes("new")) textbooks.textbook_new++;
    if (type.includes("revision")) textbooks.textbook_revision++;
  });

  result.textbooks = textbooks;

  /* ---- 11. Case publications ---- */

  const cases = {
    case_indexed: 0,
    case_nonindexed_used: 0,
    case_nonindexed: 0,
  };

  list(data.case_publications).forEach((c) => {
    if (!withinRange(c.year)) return;

    const journalType = str(c.journal_type || c.type);
    const classroom = str(c.used_for_classroom_teaching);

    if (journalType.includes("indexed")) {
      cases.case_indexed++;
    } else {
      cases.case_nonindexed++;

      if (classroom === "yes") cases.case_nonindexed_used++;
    }
  });

  result.cases = cases;

  /* ---- 12. Research grants ---- */

  const grants = {
    grant_15m: 0,
    grant_08_149: 0,
    grant_03_079: 0,
    grant_01_029: 0,
  };

  list(data.research_grant).forEach((g) => {
    if (!withinRange(g.year)) return;

    const amount =
      parseFloat((g.amount || "").toString().replace(/[^0-9.]/g, "")) || 0;

    if (amount >= 1500000) grants.grant_15m++;
    else if (amount >= 800000) grants.grant_08_149++;
    else if (amount >= 300000) grants.grant_03_079++;
    else if (amount >= 100000) grants.grant_01_029++;
  });

  result.grants = grants;

  /* ---- 13–21. Editorial services ---- */

  const editorial_editor = emptyQuartile();
  const editorial_special = emptyQuartile();
  const editorial_board = emptyQuartile();
  const editorial_reviewer = emptyQuartile();

  const conference_review = { local: 0, intl: 0 };
  const trade_editorial = { local: 0, intl: 0 };
  const trade_board = { local: 0, intl: 0 };
  const trade_review = { local: 0, intl: 0 };
  const grant_evaluation = { count: 0 };

  list(data.editorial_service).forEach((e) => {
    if (!withinRange(e.year_from)) return;

    const role = str(e.editorial_role);
    const category = str(e.category);
    const side = isBD(e.country) ? "local" : "intl";

    const quartile =
      normalizeQuartile(
        e.scopus_category || e.scopus || e.quartile || e.scopus_quartile,
      ) || "unknown";

    const isTrade = category === "trade journal";
    const isEditor = ["editor in chief", "associate editor"].includes(role);

    if (isEditor && !isTrade) editorial_editor[quartile]++;

    if (role === "special issue editor" && !isTrade)
      editorial_special[quartile]++;

    if (role === "editorial board member" && !isTrade)
      editorial_board[quartile]++;

    if (
      role === "ad-hoc reviewer" &&
      !isTrade &&
      category !== "grant/grant proposal"
    )
      editorial_reviewer[quartile]++;

    if (role === "ad-hoc reviewer" && category === "conference")
      conference_review[side]++;

    if (isEditor && isTrade) trade_editorial[side]++;

    if (role === "editorial board member" && isTrade) trade_board[side]++;

    if (role === "ad-hoc reviewer" && isTrade) trade_review[side]++;

    if (category === "grant/grant proposal") grant_evaluation.count++;
  });

  result.editorial_editor = editorial_editor;
  result.editorial_special = editorial_special;
  result.editorial_board = editorial_board;
  result.editorial_reviewer = editorial_reviewer;
  result.conference_review = conference_review;
  result.trade_editorial = trade_editorial;
  result.trade_board = trade_board;
  result.trade_review = trade_review;
  result.grant_evaluation = grant_evaluation;

  /* ---- 22–23. Research supervision ---- */

  const supervision_phd = { chair: 0, member: 0 };
  const supervision_masters = { chair: 0, member: 0 };

  list(data.research_supervision).forEach((r) => {
    if (!withinRange(r.start_year)) return;

    const degree = str(r.degree);
    const role = str(r.research_role);
    const chair = role.includes("chair") || role.includes("main");

    if (degree === "phd") {
      if (chair) supervision_phd.chair++;
      else supervision_phd.member++;
    }

    if (degree === "masters" || degree === "mphil") {
      if (chair) supervision_masters.chair++;
      else supervision_masters.member++;
    }
  });

  result.supervision_phd = supervision_phd;
  result.supervision_masters = supervision_masters;

  /* ---- 24A–24B. Proposal writing (not received) ---- */

  const proposal_writing_internal = { internal: 0 };
  const proposal_writing_external = { external: 0 };

  list(data.research_grant).forEach((g) => {
    if (!withinRange(g.year)) return;

    if (str(g.grant_status) !== "not received") return;

    const amount =
      Number((g.amount || "").toString().replace(/[^0-9.-]+/g, "")) || 0;

    const institution = str(g.institution);

    if (institution === "brac university" && amount >= 500000) {
      proposal_writing_internal.internal++;
    } else if (institution !== "brac university" && amount >= 1000000) {
      proposal_writing_external.external++;
    }
  });

  result.proposal_writing_internal = proposal_writing_internal;
  result.proposal_writing_external = proposal_writing_external;

  /* ---- 25A–25C. Conference organization ---- */

  const conference_org = { chair: 0, cochair: 0, member: 0 };

  list(
    data.academic_event_organized ||
      data.academic_events_organized ||
      data.academic_event,
  ).forEach((e) => {
    if (!withinRange(e.year)) return;

    if (e.event_type !== "Conference") return;

    if (e.role === "Chair") conference_org.chair++;
    if (e.role === "Co-Chair") conference_org.cochair++;
    if (e.role === "Committee Member") conference_org.member++;
  });

  result.conference_org = conference_org;

  /* ---- 26A–26B. Keynote / guest lecture ---- */

  const keynote = { local: 0, intl: 0 };

  list(data.conference_session_chair).forEach((r) => {
    if (!withinRange(r.year)) return;

    if (isBD(r.country)) keynote.local++;
    else keynote.intl++;
  });

  result.keynote = keynote;

  /* ---- 27. New program development ---- */

  const program_dev = { count: 0, member: 0 };

  list(data.new_program_development).forEach((r) => {
    if (!withinRange(r.year)) return;

    const role = str(r.role);

    if (role === "lead") program_dev.count++;
    if (role === "member") program_dev.member++;
  });

  result.program_dev = program_dev;

  /* ---- 28. New degree course development ---- */

  let lead_new = 0;
  let lead_revision = 0;
  let member_new = 0;
  let member_revision = 0;

  list(data.new_degree_course_development).forEach((c) => {
    if (!withinRange(c.year)) return;

    const role = str(c.role);
    const nature = str(c.nature);

    if (role === "lead" && nature === "newly developed") lead_new++;
    else if (role === "lead" && nature === "revision") lead_revision++;
    else if (role === "member" && nature === "newly developed") member_new++;
    else if (role === "member" && nature === "revision") member_revision++;
  });

  result.course_dev = {
    lead_new,
    lead_revision,
    member_new,
    member_revision,
    course_coordination: list(data.course_coordination).length,
    total: lead_new + lead_revision + member_new + member_revision,
  };

  /* ---- 29. Visiting scholar ---- */

  let visitingCount = 0;
  let visitingYears = 0;

  list(data.international_academic_contribution).forEach((r) => {
    if (!r.starting_date) return;

    const startDate = new Date(r.starting_date);
    const endDate = r.ending_date ? new Date(r.ending_date) : AACSB_END;

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return;

    if (endDate < AACSB_START || startDate > AACSB_END) return;

    const nature = str(r.nature);

    if (nature.includes("visiting") || nature.includes("scholar")) {
      visitingCount++;
      visitingYears += computeDurationCapped(startDate, endDate);
    }
  });

  result.visiting_scholar = {
    count: visitingCount,
    total_years: visitingYears,
  };

  /* ---- 30. Academic administrative roles ---- */

  result.academic_admin_role_years = {
    academic_admin_role_years: overlapYears(
      data.academic_administrative_role,
      (r) => {
        const position = str(r.position || r.role);

        return position.includes("dean") || position.includes("director");
      },
    ),
  };

  /* ---- 31. Consultation hours ---- */

  let consultationHours = 0;

  list(data.consultation_services).forEach((r) => {
    if (!r.starting_date) return;

    if (new Date(r.starting_date) > AACSB_END) return;

    if (new Date(r.ending_date || AACSB_END) < AACSB_START) return;

    consultationHours += Number(r.total_hour) || 0;
  });

  result.consultation_hours = { consultation_hours: consultationHours };

  /* ---- 32A–32B. Industry experience ---- */

  const expType = (r) =>
    normalizeExpType(
      r.experience_type ||
        r.type ||
        r.employment_type ||
        r.nature_of_experience,
    );

  result.industry_fulltime_years = {
    industry_fulltime_years: overlapYears(
      data.industry_experience,
      (r) => expType(r) === "fulltime",
    ),
  };

  result.industry_parttime_years = {
    industry_parttime_years: overlapYears(
      data.industry_experience,
      (r) => expType(r) === "parttime",
    ),
  };

  /* ---- 33. Business ownership ---- */

  result.business_ownership_years = {
    business_ownership_years: overlapYears(data.business_ownership),
  };

  /* ---- 34A–34B. Board roles ---- */

  let chairmanCount = 0;
  let boardMemberCount = 0;

  list(data.brand_membership).forEach((r) => {
    const role = str(r.role);

    if (role.includes("chairman")) chairmanCount++;
    else if (role.includes("member")) boardMemberCount++;
  });

  result.board_chairman_count = { board_chairman_count: chairmanCount };
  result.board_member_count = { board_member_count: boardMemberCount };

  /* ---- 35. Organizational membership ---- */

  let organizationalMembership = 0;

  list(data.organizational_membership).forEach((r) => {
    const start = r.starting_month_year || r.starting_date;
    const end = r.ending_month_year || r.ending_date;

    if (!start) return;

    if (
      new Date(end || AACSB_END) < AACSB_START ||
      new Date(start) > AACSB_END
    ) {
      return;
    }

    organizationalMembership++;
  });

  result.organizational_membership_count = {
    organizational_membership_count: organizationalMembership,
  };

  /* ---- 36A–36B. Invited speakers ---- */

  let speakerIntl = 0;
  let speakerLocal = 0;

  list(data.speech_delivered).forEach((r) => {
    if (!withinRange(r.starting_date || r.date || r.year)) return;

    if (isBD(r.country)) speakerLocal++;
    else speakerIntl++;
  });

  result.speaker_intl_count = { speaker_intl_count: speakerIntl };
  result.speaker_local_count = { speaker_local_count: speakerLocal };

  /* ---- 37. Training ---- */

  result.training_sessions_count = {
    training_sessions_count: list(data.training_delivered).filter((r) =>
      withinRange(r.starting_date || r.date || r.year),
    ).length,
  };

  /* ---- 38. Media ---- */

  result.media_contributions_count = {
    media_contributions_count: list(data.contribution_to_media).filter(
      (r) =>
        str(r.represented_bracu) === "yes" &&
        withinRange(r.year || r.date || r.contribution_date),
    ).length,
  };

  /* ---- 39A / 39B / 40. Professional certifications ---- */

  let profCertIntl = 0;
  let profCertLocal = 0;
  let profCertYears = 0;

  list(data.professional_certification).forEach((r) => {
    const year = r.awarding_year || r.year;

    if (!withinRange(year)) return;

    if (isBD(r.country)) profCertLocal++;
    else profCertIntl++;

    if (r.awarding_year) {
      const start = new Date(Number(r.awarding_year), 0, 1);

      const end = r.ending_year
        ? new Date(Number(r.ending_year), 11, 31)
        : AACSB_END;

      profCertYears += computeDurationCapped(start, end);
    }
  });

  result.prof_cert_intl_count = { prof_cert_intl_count: profCertIntl };
  result.prof_cert_local_count = { prof_cert_local_count: profCertLocal };
  result.prof_cert_total_years = { prof_cert_total_years: profCertYears };

  /* ---- 41. New executive courses ---- */

  result.new_exec_courses_count = {
    count: list(data.professional_certificate_development).filter(
      (r) =>
        str(r.nature) === "newly developed" &&
        withinRange(r.starting_date || r.year),
    ).length,
  };

  /* ---- 42. Policy advisory roles ---- */

  result.policy_advisory_roles_count = {
    policy_advisory_roles_count: list(data.policy_advisory_role).filter(
      (r) =>
        withinRange(r.starting_date) &&
        computeDurationCapped(r.starting_date, r.ending_date || AACSB_END) > 0,
    ).length,
  };

  /* ---- 43A–43B. Policy briefs ---- */

  let policyBriefIntl = 0;
  let policyBriefLocal = 0;

  list(data.policy_briefs).forEach((r) => {
    if (!withinRange(r.year)) return;

    if (isBD(r.country)) policyBriefLocal++;
    else policyBriefIntl++;
  });

  result.policy_briefs_intl = { policy_briefs_intl: policyBriefIntl };
  result.policy_briefs_local = { policy_briefs_local: policyBriefLocal };

  /* ---- 44. IP commercialization ---- */

  result.ip_commercialization_count = {
    ip_commercialization_count: list(data.intellectual_property).filter((r) =>
      withinRange(r.year || r.date || r.registration_year),
    ).length,
  };

  /* ---- 45. Pro-bono ---- */

  result.pro_bono_count = {
    pro_bono_count: list(data.public_interest_engagement).filter((r) =>
      withinRange(r.starting_date),
    ).length,
  };

  return result;
};

/* ===================== COMPONENT ===================== */

export default function AacsbDashboard() {
  const navigate = useNavigate();

  const facultyId = localStorage.getItem("aacsbPin");

  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ============================================================
     LOAD FACULTY DATA + AUTHENTICATED BOOK REVIEW DATA
     ============================================================ */

  useEffect(() => {
    const loadAacsbData = async () => {
      try {
        const savedFacultyData = JSON.parse(
          localStorage.getItem("facultyData"),
        );

        if (!savedFacultyData) {
          toast.error("No faculty data found");
          return;
        }

        const token = localStorage.getItem("token");

        if (!token) {
          toast.error("Authentication token not found");
          return;
        }

        const authHeaders = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        // BOOK REVIEW INTERNATIONAL

        const internationalResponse = await fetch(
          `${BACKEND_URL}/api/book_review_international`,
          { method: "GET", headers: authHeaders },
        );

        if (!internationalResponse.ok) {
          throw new Error(
            `Book Review International API failed: ${internationalResponse.status}`,
          );
        }

        const internationalResult = await internationalResponse.json();

        // BOOK REVIEW LOCAL

        const localResponse = await fetch(
          `${BACKEND_URL}/api/book_review_local`,
          { method: "GET", headers: authHeaders },
        );

        if (!localResponse.ok) {
          throw new Error(
            `Book Review Local API failed: ${localResponse.status}`,
          );
        }

        const localResult = await localResponse.json();

        // Supports both [ {...} ] and { data: [ {...} ] }

        const internationalReviews = Array.isArray(internationalResult)
          ? internationalResult
          : Array.isArray(internationalResult?.data)
          ? internationalResult.data
          : [];

        const localReviews = Array.isArray(localResult)
          ? localResult
          : Array.isArray(localResult?.data)
          ? localResult.data
          : [];

        /*
         * Keep the faculty PIN with the data passed to computeMetrics().
         * The API returns book-review records for multiple faculties, so
         * computeMetrics() uses this PIN to count only this faculty's rows.
         */
        const data = {
          ...savedFacultyData,
          __facultyPin: facultyId,
          book_review_international: internationalReviews,
          book_review_local: localReviews,
        };

        const computed = computeMetrics(data);

        // Keep the original facultyData structure unchanged in localStorage.
        localStorage.setItem(
          "facultyData",
          JSON.stringify({
            ...savedFacultyData,
            book_review_international: internationalReviews,
            book_review_local: localReviews,
          }),
        );

        setMetrics(computed);

        localStorage.setItem("aacsbMetrics", JSON.stringify(computed));
      } catch (error) {
        console.error("AACSB Dashboard data loading error:", error);

        toast.error(error?.message || "AACSB metric computation failed");
      } finally {
        setLoading(false);
      }
    };

    loadAacsbData();
  }, []);

  if (loading) return <p style={{ textAlign: "center" }}>Loading…</p>;

  if (!metrics) return <p>No data available</p>;

  const renderSection = (title, key) => {
    const schema = SECTION_COLUMN_SCHEMA[key];

    const data = metrics[key];

    if (!schema || data === undefined) return null;

    /*
     * Only the Book Review labels are dynamically
     * changed to display the authenticated API totals.
     */
    const displaySchema =
      key === "book_review"
        ? schema.map((column) => {
            if (column.key === "book_review_intl") {
              return {
                ...column,
                label: `Book Review International (Total: ${
                  metrics.book_review?.book_review_intl ?? 0
                })`,
              };
            }

            if (column.key === "book_review_local") {
              return {
                ...column,
                label: `Book Review Local (Total: ${
                  metrics.book_review?.book_review_local ?? 0
                })`,
              };
            }

            return column;
          })
        : schema;

    return (
      <div style={{ marginTop: 20 }}>
        <h3
          style={{
            background: "#1d4786a1",
            color: "#fff",
            padding: 8,
          }}
        >
          {title}
        </h3>

        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr>
              {displaySchema.map((c) => (
                <th
                  key={c.key}
                  style={{
                    border: "1px solid #000",
                    padding: 8,
                  }}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            <tr>
              {displaySchema.map((c) => (
                <td
                  key={c.key}
                  style={{
                    border: "1px solid #000",
                    padding: 8,
                  }}
                >
                  {(() => {
                    const value = data[c.key];

                    if (typeof value === "number") return value.toFixed(2);

                    if (value && typeof value === "object") {
                      const count = Number(value.count ?? 0);

                      const years = value.total_years;

                      return years !== undefined
                        ? `${count} (Total Years: ${years.toFixed(2)})`
                        : `${count}`;
                    }

                    return value ?? 0;
                  })()}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const getSafeExcelFileName = () => {
    try {
      const saved = JSON.parse(localStorage.getItem("facultyData"));

      const info = saved?.personal_information?.[0];

      if (!info) return "AACSB_Metrics.xlsx";

      const fullName = info
        ? `${info.salutation?.trim() || ""} ${info.name?.trim() || ""}`.trim()
        : "Faculty_Profile";

      const safeName = fullName
        .replace(/\./g, "")
        .replace(/\s+/g, "_")
        .replace(/[^\w_]/g, "");

      return `${safeName}_AACSB_Metrics.xlsx`;
    } catch {
      return "AACSB_Metrics.xlsx";
    }
  };

  const exportToExcel = () => {
    if (!metrics) {
      alert("No data available");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = {};

    let row = 0;

    // ===== Styles =====

    const sectionStyle = {
      font: { bold: true, sz: 13 },
      fill: { fgColor: { rgb: "DCE6F1" } },
      alignment: { vertical: "center", horizontal: "left" },
      border: {
        top: { style: "thick" },
        bottom: { style: "thick" },
      },
    };

    const headerStyle = {
      font: { bold: true },
      fill: { fgColor: { rgb: "F2F2F2" } },
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      },
    };

    const cellStyle = {
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      },
    };

    // ===== Section order EXACTLY as UI =====

    const sections = [
      ["Scholarly Publications (1A–1E)", "journals"],
      ["Scholarly Books Published (2A–2B)", "books"],
      ["Edited Book Chapters Published (3A–3B)", "chapters"],
      ["Book Review (4A–4B)", "book_review"],
      [
        "Published Conference Proceedings & Research Papers Presented (5A–6B)",
        "conferences",
      ],
      ["Renowned Non-Academic/Trade Journals (7A–7B)", "trade"],
      ["Industry Reports & Press Articles (8–9)", "others"],
      ["Textbooks (10A–10B)", "textbooks"],
      ["Case Publications (11A–11C)", "cases"],
      ["Research Grants (12A–12D)", "grants"],
      ["Editorial Editor (13A–13E)", "editorial_editor"],
      ["Special Issue Editor (14A–14E)", "editorial_special"],
      ["Editorial Board Member (15A–15E)", "editorial_board"],
      ["Editorial Reviewer (16A–16E)", "editorial_reviewer"],
      ["Conference Reviewer (17A–17B)", "conference_review"],
      ["Trade Journal Editorial (18A–18B)", "trade_editorial"],
      ["Trade Journal Board (19A–19B)", "trade_board"],
      ["Trade Journal Reviewer (20A–20B)", "trade_review"],
      ["Grant Evaluation (21)", "grant_evaluation"],
      ["Doctoral Supervision (22A–22B)", "supervision_phd"],
      ["Masters Supervision (23A–23B)", "supervision_masters"],
      ["External Proposal Writing (24A)", "proposal_writing_external"],
      ["Internal Proposal Writing (24B)", "proposal_writing_internal"],
      ["Conference Organization (25A–25C)", "conference_org"],
      ["Keynote / Guest Lecture (26A–26B)", "keynote"],
      ["New Programs Developed (27)", "program_dev"],
      ["New Degree Courses Developed (28)", "course_dev"],
      ["Visiting Scholar (29)", "visiting_scholar"],
      ["Academic Admin Roles (30)", "academic_admin_role_years"],
      ["Consultation Hours (31)", "consultation_hours"],
      ["Industry Experience Full-time (32A)", "industry_fulltime_years"],
      ["Industry Experience Part-time (32B)", "industry_parttime_years"],
      ["Business Ownership (33)", "business_ownership_years"],
      ["Board Chairman (34A)", "board_chairman_count"],
      ["Board Member (34B)", "board_member_count"],
      ["Organizational Membership (35)", "organizational_membership_count"],
      ["Invited Speaker Intl (36A)", "speaker_intl_count"],
      ["Invited Speaker Local (36B)", "speaker_local_count"],
      ["Training Sessions (37)", "training_sessions_count"],
      ["Media Contributions (38)", "media_contributions_count"],
      ["Professional Certifications Intl (39A)", "prof_cert_intl_count"],
      ["Professional Certifications Local (39B)", "prof_cert_local_count"],
      ["Certification Maintenance Years (40)", "prof_cert_total_years"],
      ["New Executive Courses (41)", "new_exec_courses_count"],
      ["Policy Advisory Roles (42)", "policy_advisory_roles_count"],
      ["Policy Briefs Intl (43A)", "policy_briefs_intl"],
      ["Policy Briefs Local (43B)", "policy_briefs_local"],
      ["IP Commercialization (44)", "ip_commercialization_count"],
      ["Pro-bono Services (45)", "pro_bono_count"],
      ["Total Collaborative Works", "collaborative_count"],
    ];

    sections.forEach(([title, key]) => {
      const schema = SECTION_COLUMN_SCHEMA[key];

      const data = metrics[key];

      if (!schema || !data) return;

      // Section title

      ws[`A${row + 1}`] = { v: title, s: sectionStyle };
      ws[`B${row + 1}`] = { v: "", s: sectionStyle };

      row++;

      // Header row

      ws[`A${row + 1}`] = { v: "Metric", s: headerStyle };
      ws[`B${row + 1}`] = { v: "Value", s: headerStyle };

      row++;

      schema.forEach((col) => {
        let val = data[col.key];

        if (typeof val === "object" && val !== null) {
          if ("count" in val || "total_years" in val) {
            const c = val.count ?? "";

            const y = val.total_years;

            val = y !== undefined ? `${c} (Years: ${y.toFixed(2)})` : `${c}`;
          } else {
            val = Object.entries(val)
              .map(([k, v]) => `${k}: ${v}`)
              .join(", ");
          }
        }

        if (key === "book_review" && col.key === "book_review_intl") {
          val = metrics.book_review?.book_review_intl ?? 0;
        }

        if (key === "book_review" && col.key === "book_review_local") {
          val = metrics.book_review?.book_review_local ?? 0;
        }

        ws[`A${row + 1}`] = { v: col.label, s: cellStyle };
        ws[`B${row + 1}`] = { v: val ?? 0, s: cellStyle };

        row++;
      });

      row++;
    });

    ws["!cols"] = [{ wch: 70 }, { wch: 25 }];

    ws["!ref"] = `A1:B${row}`;

    XLSX.utils.book_append_sheet(wb, ws, "AACSB Metrics");

    XLSX.writeFile(wb, getSafeExcelFileName());
  };

  return (
    <div
      style={{
        padding: 20,
        backgroundImage: `url(${img})`,
        minHeight: "100vh",
      }}
    >
      <button
        onClick={exportToExcel}
        style={{
          marginBottom: "20px",
          padding: "8px 16px",
          fontSize: "1rem",
          backgroundColor: "#28a745",
          color: "#fff",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
        }}
      >
        📥 Export to Excel
      </button>

      <div
        style={{
          marginBottom: "20px",
        }}
      >
        <ToastContainer position="top-center" autoClose={1500} />

        <button
          onClick={() => {
            toast.info("Going back...");

            setTimeout(() => window.history.back(), 500);
          }}
          style={{
            padding: "8px 16px",
            fontSize: "1rem",
            backgroundColor: "#1d4786",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          ← Back
        </button>

        <button
          onClick={() => navigate(`/aacsb-raw/${facultyId}`)}
          style={{
            padding: "8px 16px",
            fontSize: "1rem",
            backgroundColor: "#111827",
            marginLeft: "14px",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          AACSB Classification
        </button>
      </div>

      <ToastContainer position="top-center" autoClose={3000} />

      <h2>AACSB Metrics (1–45)</h2>

      {renderSection("Scholarly Publications (1A–1E)", "journals")}

      {renderSection("Scholarly Books Published (2A–2B)", "books")}

      {renderSection("Edited Book Chapters Published (3A–3B)", "chapters")}

      {renderSection("Book Review (4A–4B)", "book_review")}

      {renderSection(
        "Published Conference Proceedings & Research Papers Presented  (5A–6B)",
        "conferences",
      )}

      {renderSection("Renowned Non-Academic/Trade Journals(7A–7B)", "trade")}

      {renderSection(
        "Industry Reports & Press Articles (Op-Eds/Editorials) (8–9)",
        "others",
      )}

      {renderSection(
        "Textbooks (relevant to the field of teaching) (10A–10B)",
        "textbooks",
      )}

      {renderSection("Case Publications (11A–11C)", "cases")}

      {renderSection("Research Grants (12A–12D)", "grants")}

      {renderSection(
        "Editor/associate editor positions held in a Scopus-indexed journal(13A–13E)",
        "editorial_editor",
      )}

      {renderSection(
        "Times served as a Special Issue Editor of a Scopus-indexed journal (14A–14E)",
        "editorial_special",
      )}

      {renderSection(
        "Editorial Board Member positions held of a Scopus-indexed journal (15A–15E)",
        "editorial_board",
      )}

      {renderSection(
        "Editorial Reviewer as an ad-hoc reviewer of a Scopus-indexed journal (16A–16E)",
        "editorial_reviewer",
      )}

      {renderSection("Reviewer in Conferences (17A–17B)", "conference_review")}

      {renderSection(
        "Editorial Service of Trade Journals (18A–18B)",
        "trade_editorial",
      )}

      {renderSection(
        "Editorial Board Member of Non-Academic/Trade Journals (19A–19B)",
        "trade_board",
      )}

      {renderSection(
        "Papers Reviewed as an ad-hoc reviewer of Trade Journals (20A–20B)",
        "trade_review",
      )}

      {renderSection(
        "Evaluation of Research Grant Applications as an ad-hoc reviewer (21)",
        "grant_evaluation",
      )}

      {renderSection(
        "Doctoral Degree Thesis Supervision as (22A–22B)",
        "supervision_phd",
      )}

      {renderSection(
        "Masters Degree Thesis Supervision as (23A–23B)",
        "supervision_masters",
      )}

      {renderSection(
        "External Funding Proposal Writing & Submission(24A)",
        "proposal_writing_external",
      )}

      {renderSection(
        "Internal Funding Proposal Writing & Submission (24B)",
        "proposal_writing_internal",
      )}

      {renderSection("Academic Conferences Organized as (25A–25C)", "conference_org")}

      {renderSection(
        "Times invited for Guest Lecture or Keynote Speech (26A–26B)",
        "keynote",
      )}

      {renderSection("New Programs Developed (27)", "program_dev")}

      {renderSection("New Degree Courses Developed (28)", "course_dev")}

      {renderSection("Visiting Scholar Duration (Years) (29)", "visiting_scholar")}

      {renderSection(
        "Years of experience as Dean/Associate Dean/Assistant Dean/Program Director/Research Director/Other Directors (30)",
        "academic_admin_role_years",
      )}

      {renderSection(
        "Total hours of Consultation Service to the industry/development entities  (31)",
        "consultation_hours",
      )}

      {renderSection(
        "Full-time practitioner job (Industry) Experience (32A)",
        "industry_fulltime_years",
      )}

      {renderSection(
        "Part-time practitioner job  (Industry) Experience (32B)",
        "industry_parttime_years",
      )}

      {renderSection("Years of Business Ownership (33)", "business_ownership_years")}

      {renderSection("Times sereved as a Board Chairman (34A)", "board_chairman_count")}

      {renderSection("Times served as a Board Member (34B)", "board_member_count")}

      {renderSection(
        "High-ranked positions held in a Professional Association (Organizational Membership) (35)",
        "organizational_membership_count",
      )}

      {renderSection(
        "Times of invited expert/speaker at an established company (International) (36A)",
        "speaker_intl_count",
      )}

      {renderSection(
        "Times of invited expert/speaker at an established company (Loacal)",
        "speaker_local_count",
      )}

      {renderSection("Training Sessions Delivered (37)", "training_sessions_count")}

      {renderSection(
        "Times Contributed to Media (representing BRACU/BBS) (38)",
        "media_contributions_count",
      )}

      {renderSection(
        "Professional Certifications (international certification body) (39A)",
        "prof_cert_intl_count",
      )}

      {renderSection(
        "Professional Certifications (local certification body) (39B)",
        "prof_cert_local_count",
      )}

      {renderSection(
        "Years of Maintenance of Professional Certifications(40)",
        "prof_cert_total_years",
      )}

      {renderSection(
        "New Executive Education Courses Developed (41)",
        "new_exec_courses_count",
      )}

      {renderSection("Formal Policy Advisory Roles (42)", "policy_advisory_roles_count")}

      {renderSection(
        "White Papers/Policy Briefs for public institutions (International) (43A)",
        "policy_briefs_intl",
      )}

      {renderSection("White Papers/Policy Briefs (Local) (43B)", "policy_briefs_local")}

      {renderSection(
        "Commercialization of Intellectual Property (44)",
        "ip_commercialization_count",
      )}

      {renderSection(
        "Pro-bono Professional Services for Public-Interest Institutions (45)",
        "pro_bono_count",
      )}

      {renderSection("Total Number of Collaborative Works", "collaborative_count")}
    </div>
  );
}
